import { MarketAdjustmentWritable, MarketComparisonWritable } from 'api';
import { ONE_HUNDRED_VALUE } from 'common/config/app';
import { Option } from 'pages/FundInvestments';
import { FUND_INVESTMENTS_SLUG, FUND_INVESTMENTS_TITLE_SINGULAR } from 'pages/FundInvestments/common/constants/fundInvestmentsBase';
import { PercentileSelection } from 'pages/FundInvestments/pages/FundInvestmentMarketAdjustment';
import { dbShortDate, getNumberValue, getSlugValue } from 'utilities';
import { GetFundInvestmentNewMarketAdjustment } from './fundInvestmentMarketAdjustment.types';

const { adjustment_selection: ADJUSTMENT_SELECTION, metric_selection: METRIC_SELECTION } = MarketAdjustmentWritable;
const { SELECTION_A: PERCENTILE_A, SELECTION_B: PERCENTILE_B } = ADJUSTMENT_SELECTION;

// Titles
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE = 'Market Adjustment';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SLUG = getSlugValue(FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE);
export const FUND_INVESTMENTS_COMPARABLES_TITLE = 'Comparables';
export const FUND_INVESTMENTS_COMPARABLES_TITLE_SINGULAR = 'Comparable';
export const FUND_INVESTMENTS_ADD_COMPARABLE_TITLE = `Add ${FUND_INVESTMENTS_COMPARABLES_TITLE_SINGULAR}`;
export const FUND_INVESTMENTS_ADD_COMPARABLE_SLUG = getSlugValue(FUND_INVESTMENTS_ADD_COMPARABLE_TITLE);
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_PRICE_TITLE = 'Price as of Measurement Date';
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_TYPE_TITLE = 'Type';
export const FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE = 'Indexes/ETFs/Companies';
export const FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE_SINGULAR = 'Index/ETF/Company';
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_TITLE = `Add ${FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE_SINGULAR}`;
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_NAME_OR_TICKER_TITLE = 'Name or Ticker';
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_MULTIPLE_COMPANY_TICKERS_TITLE = 'Enter Multiple Company Tickers';
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_MULTIPLE_INDEX_TICKERS_TITLE = 'Enter Multiple Index/ETF Tickers';
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_INDEXES_ETFS_FUNDS_COMPANIES_TITLE
  = 'Indexes, ETFs, Funds, or Companies';
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_INDEXES_ETFS_FUNDS_COMPANIES_TITLE_SINGULAR
  = 'Index, ETF, Fund, or Company';
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_SEARCHING_TITLE = `Type in the box to search for ${FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_INDEXES_ETFS_FUNDS_COMPANIES_TITLE}`;
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_NO_FOUND_TITLE = `No ${FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_INDEXES_ETFS_FUNDS_COMPANIES_TITLE} found. Please enter another search term and try again`;
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_ONE_FOUND_TITLE = '1 company found. Select it to add it';
export const FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_MULTIPLE_FOUND_TITLE = `${FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_INDEXES_ETFS_FUNDS_COMPANIES_TITLE} found. Select a ${FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_INDEXES_ETFS_FUNDS_COMPANIES_TITLE_SINGULAR} to add`;
export const getFundInvestmentsGpcDialogMultipleFoundTitle = (count: number) =>
  `${count} ${FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_MULTIPLE_FOUND_TITLE}`;
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_FIELD_ATTRIBUTES = 'valuations.marketadjustment';
export const FUND_INVESTMENTS_MARKET_COMPARISON_FIELD_ATTRIBUTES = 'valuations.marketcomparison';
export const FUND_INVESTMENTS_PUBLIC_COMPS_INDEX_TYPES = Object.freeze(['Index', 'Public Fund']);
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_CONTAINER_CLASSNAME = `${FUND_INVESTMENTS_SLUG}-${FUND_INVESTMENTS_MARKET_ADJUSTMENT_SLUG}-container`;

// Spreadsheet
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_NAME = 'Column';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_NAME_PLURAL = 'Columns';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLE = `${FUND_INVESTMENTS_TITLE_SINGULAR} ${FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE}`;
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SLUG = getSlugValue(
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLE
);
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME = `${FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SLUG}-spreadsheet`;

export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TABLE_TERMS = Object.freeze({
  columnName: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_NAME,
  pluralColumnName: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_NAME_PLURAL,
  tableName: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NAME,
  tableSlug: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SLUG,
});

// Aliases
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLE_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLE';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_DATES_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_DATES';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TITLE_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TITLE';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_INDEX_TITLE_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_INDEX_TITLE';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_WEIGHTED_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_WEIGHTED';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEDIAN_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEDIAN';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEAN_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEAN';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_A_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_A';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_B_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_B';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SPECIFIED_ADJUSTMENT_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SPECIFIED_ADJUSTMENT';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ADJUSTMENT_SELECTION_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ADJUSTMENT_SELECTION';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MARKET_ADJUSTMENT_ALIAS
  = 'FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MARKET_ADJUSTMENT';

// Columns
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMNS = Object.freeze({
  public_company_index_etf: {
    column: 0,
    title: 'Comparison',
    tooltip: '',
  },
  ticker: {
    column: 1,
    title: 'Ticker',
    tooltip: '',
  },
  market_cap: {
    column: 2,
    title: 'Market Cap',
    tooltip: '',
  },
  metric_selection_as_of_date: {
    column: 3,
    title: 'Price',
    tooltip: '',
  },
  metric_selection_as_of_market_date: {
    column: 4,
    title: 'Price',
    tooltip: '',
  },
  percentage_change: {
    column: 5,
    title: 'Percentage Change',
    tooltip: '',
  },
  weighting: {
    column: 6,
    title: 'Weighting',
    tooltip: '',
  },
});

// Calculations columns
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_CALCULATIONS_COLUMNS = Object.freeze({
  as_of_date: {
    key: 'as_of_date',
    optionKey: '',
    title: 'As of Date',
    tooltip: '',
  },
  metric_selection: {
    key: 'metric_selection',
    optionKey: '',
    title: 'Metric Selection',
    tooltip: '',
  },
  weighted: {
    key: 'weighted',
    optionKey: '',
    title: 'Weighted',
    tooltip: 'Percentage Change exclusions are not applied to the Weighted value',
  },
  median: {
    key: 'median',
    optionKey: '',
    title: 'Median',
    tooltip: '',
  },
  mean: {
    key: 'mean',
    optionKey: '',
    title: 'Mean',
    tooltip: '',
  },
  percentile_selection_a: {
    key: 'percentile_selection_a',
    optionKey: 'percentile_selection_a_option',
    title: PERCENTILE_A,
    tooltip: '',
  },
  percentile_selection_b: {
    key: 'percentile_selection_b',
    optionKey: 'percentile_selection_b_option',
    title: PERCENTILE_B,
    tooltip: '',
  },
  specified_adjustment: {
    key: 'specified_adjustment',
    optionKey: '',
    title: 'Specified Adjustment',
    tooltip: '',
  },
  adjustment_selection: {
    key: 'adjustment_selection',
    optionKey: '',
    title: 'Select Adjustment',
    tooltip: '',
  },
  market_adjustment: {
    key: 'market_adjustment',
    optionKey: '',
    title: 'Applied Market Adjustment',
    tooltip: '',
  },
});

export const getFundInvestmentMarketAdjustmentColumn = (columnNumber: number) => {
  const columnsArray = Object.values(FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMNS);

  return columnsArray?.[columnNumber];
};

// Metrics
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS_OPTIONS = Object.freeze(
  Object.entries(METRIC_SELECTION).map(
    ([key, value]) =>
      ({
        label: value,
        value: key.toLowerCase(),
      }) as Option
  )
);

export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS = Object.freeze(
  Object.fromEntries(Object.entries(METRIC_SELECTION).map(([key, value]) => [key.toLowerCase(), value]))
);

export const getFundInvestmentMarketAdjustmentMetricKey = (
  label: MarketAdjustmentWritable.metric_selection
): keyof typeof FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS | null =>
  Object.entries(FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS).find(([, value]) => value === label)?.[0]
  ?? null;

export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS_KEYS = Object.keys(
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS
);

// Percentiles
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_A = 75;
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_B = 25;

export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILES: PercentileSelection = Object.freeze({
  percentile_selection_a: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_A,
  percentile_selection_b: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_B,
});

export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ADJUSTMENT_SELECTIONS_VALUES
  = Object.values(ADJUSTMENT_SELECTION);

// Company types
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TYPES = Object.freeze({
  public_company: {
    title: 'Public Company',
    key: 'Public Company',
  },
  index_etf: {
    title: 'Index/ETF',
    key: FUND_INVESTMENTS_PUBLIC_COMPS_INDEX_TYPES.join(' / '),
  },
});

// Weighting
export const FUND_INVESTMENTS_TOTAL_WEIGHTING_TITLE = 'Total weighting';
export const FUND_INVESTMENTS_WEIGHTINGS_GREATER_THAN_100 = `The sum of weightings is greater than ${ONE_HUNDRED_VALUE}%`;
export const FUND_INVESTMENTS_WEIGHTINGS_LOWER_THAN_100 = `The sum of weightings is lower than ${ONE_HUNDRED_VALUE}%`;
export const FUND_INVESTMENTS_WEIGHTING_VALIDATIONS = Object.freeze({
  [FUND_INVESTMENTS_WEIGHTINGS_GREATER_THAN_100]: FUND_INVESTMENTS_WEIGHTINGS_LOWER_THAN_100,
  [FUND_INVESTMENTS_WEIGHTINGS_LOWER_THAN_100]: FUND_INVESTMENTS_WEIGHTINGS_GREATER_THAN_100,
});

// Default values
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ALLOW_NEGATIVE_VALUE = true;
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ROW_SPAN = 1;
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_KEY_PREFIX = getSlugValue(
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMNS.public_company_index_etf.title
);
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLES_CLASSNAME
  = 'subtitle-bold division-bottom-only show-tooltip';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_CALCULATIONS_CLASSNAME = 'text-left show-tooltip';
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_PUBLIC_COMPANY_ROWS = Object.freeze([
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLE_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_DATES_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TITLE_ALIAS,
]);
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_PUBLIC_COMPANY_ROWS_COUNT = getNumberValue(
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_PUBLIC_COMPANY_ROWS.length
);
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_INDEX_ETF = Object.freeze([
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_INDEX_TITLE_ALIAS,
]);
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_INDEX_ETF_COUNT = getNumberValue(
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_INDEX_ETF.length
);
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EMPTY_TERMS = Object.freeze({
  columnName: FUND_INVESTMENTS_COMPARABLES_TITLE_SINGULAR,
  pluralColumnName: FUND_INVESTMENTS_COMPARABLES_TITLE,
  tableName: FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE,
  tableSlug: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SLUG,
});
export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NEW_MARKET_ADJUSTMENT = Object.freeze({
  adjustment_selection: MarketAdjustmentWritable.adjustment_selection.MEDIAN,
  as_of_date: '',
  deleted_market_comparisons: [],
  id: undefined, // Assigned by the backend
  market_adjustment: null,
  market_comparisons: [],
  metric_selection: MarketAdjustmentWritable.metric_selection.MARKET_CAP,
  name: FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE, // This value cannot be empty
  percentile_selection_a: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_A,
  percentile_selection_b: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_B,
  specified_adjustment: null,
  valuation: 0,
} as MarketAdjustmentWritable);

export const getFundInvestmentNewMarketAdjustment: GetFundInvestmentNewMarketAdjustment = params => {
  const { asOfDate, valuationId } = params;

  return {
    ...FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NEW_MARKET_ADJUSTMENT,
    as_of_date: dbShortDate(asOfDate),
    valuation: valuationId,
  } as MarketAdjustmentWritable;
};

export const FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NEW_MARKET_COMPARISON = Object.freeze({
  as_of_date: '',
  business_description: '',
  cap_iq_id: '',
  close_price: '0',
  company_name: '',
  company_type: '',
  currency: '',
  enabled: true,
  enterprise_value_excluding_operating_leases: '0',
  enterprise_value: '0',
  forward_net_income: '0',
  id: undefined, // Assigned by the backend
  is_index: false,
  ltm_ebitda: '0',
  ltm_revenue_growth: '0',
  ltm_revenue: '0',
  market_adjustment: null, // Assigned by the backend
  market_cap: '0',
  net_income_to_common_excl_extra_items: '0',
  net_income: '0',
  ntm_ebitda: '0',
  ntm_revenue_growth: '0',
  ntm_revenue: '0',
  order: 0,
  tangible_book_value_estimate: '0',
  tangible_book_value: '0',
  ticker_symbol: '',
  weight: '0',
} as MarketComparisonWritable);
